import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import dayjs from "dayjs";
import { Section } from "../../sharedComponents/Section";
import * as styles from "./CountdownBanner.module.scss";

const CountdownBanner = ({ endDate, message, colorVariant, sectionPadding, labels }) => {
	const calculateTimeLeft = () => {
		const now = dayjs();
		const end = dayjs(endDate);
		const diff = end.diff(now, "second");

		if (diff <= 0) {
			return {
				days: 0,
				hours: 0,
				minutes: 0,
				seconds: 0,
			};
		}

		return {
			days: Math.floor(diff / (60 * 60 * 24)),
			hours: Math.floor((diff / (60 * 60)) % 24),
			minutes: Math.floor((diff / 60) % 60),
			seconds: Math.floor(diff % 60),
		};
	};

	const initialTimeLeft = calculateTimeLeft();
	const initialIsVisible = !(
		initialTimeLeft.days === 0 &&
		initialTimeLeft.hours === 0 &&
		initialTimeLeft.minutes === 0 &&
		initialTimeLeft.seconds === 0
	);

	const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
	const [isVisible, setIsVisible] = useState(initialIsVisible);

	useEffect(() => {
		const updateTimeLeft = () => {
			const newTimeLeft = calculateTimeLeft();

			if (
				newTimeLeft.days === 0 &&
				newTimeLeft.hours === 0 &&
				newTimeLeft.minutes === 0 &&
				newTimeLeft.seconds === 0
			) {
				setIsVisible(false);
				return;
			}

			setTimeLeft(newTimeLeft);
		};

		const timer = setInterval(updateTimeLeft, 1000);
		updateTimeLeft();

		return () => clearInterval(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endDate]);

	if (!isVisible) return null;

	return (
		<Section padding={sectionPadding}>
			<div className={cx(styles.countdownBanner, styles[colorVariant])}>
				<div className={styles.countdownBannerMessage}>{message}</div>
				<div className={styles.countdownBannerCountdown}>
					<div>
						<span className={styles.countdownBannerDisplay}>
							{String(timeLeft.days).padStart(2, "0")}
						</span>
						<div className={styles.countdownBannerText}>{labels.days}</div>
					</div>
					<div>
						<span className={styles.countdownBannerDisplay}>
							{String(timeLeft.hours).padStart(2, "0")}
						</span>
						<div className={styles.countdownBannerText}>{labels.hours}</div>
					</div>
					<div>
						<span className={styles.countdownBannerDisplay}>
							{String(timeLeft.minutes).padStart(2, "0")}
						</span>
						<div className={styles.countdownBannerText}>{labels.minutes}</div>
					</div>
					<div>
						<span className={styles.countdownBannerDisplay}>
							{String(timeLeft.seconds).padStart(2, "0")}
						</span>
						<div className={styles.countdownBannerText}>{labels.seconds}</div>
					</div>
				</div>
			</div>
		</Section>
	);
};

CountdownBanner.propTypes = {
	endDate: PropTypes.string.isRequired,
	message: PropTypes.node.isRequired,
	colorVariant: PropTypes.oneOf(["pinkOrchid", "coralReef", "seaFoam"]),
	sectionPadding: PropTypes.oneOf(["top", "bottom", "both", "none"]),
	labels: PropTypes.shape({
		days: PropTypes.node,
		hours: PropTypes.node,
		minutes: PropTypes.node,
		seconds: PropTypes.node,
	}).isRequired,
};

CountdownBanner.defaultProps = {
	colorVariant: "pinkOrchid",
	sectionPadding: "bottom",
};

export default CountdownBanner;
export { CountdownBanner };
